import { getExternalResources } from "@/shared/utils";
import { Editor } from "grapesjs";
import { FontOption, FontProperty } from "../modals/FontSelectorModal";

export const addHandlers = (editor: Editor) => {
  editor.on("canvas:frame:load", () => {
    const injectExternalResourcesHTML = () => {
      const doc = editor.Canvas.getDocument();
      if (!doc) {
        // This happens while grapesjs is not ready
        return;
      }
      const data = editor.getProjectData();
      const externalJS = getExternalResources(data)
        .javascript.map((url) => `<script src="${url}"></script>`)
        .join("");

      const externalCSS = getExternalResources(data)
        .css.map((url) => `<link rel="stylesheet" href="${url}" />`)
        .join("");

      const html = externalCSS + externalJS;
      doc.head.insertAdjacentHTML("beforeend", html);
    };
    // Move to the bottom of JS event loop
    setTimeout(injectExternalResourcesHTML, 0);
  });

  editor.on("block:drag:stop", (component, block) => {
    if (block && block.id === "navbar" && component) {
      // Find the brand link (usually the first child of the navbar container)
      const brand = component.findType("link")[0];
      if (brand) {
        const parent = brand.parent();
        const index = brand.index();
        // Remove the brand link
        brand.remove();
        // Add a new link-block at the same position
        parent.append(
          {
            type: "link-block",
            attributes: {
              class: brand.getAttributes().class, // preserve class if needed
              href: brand.getAttributes().href || "#",
            },
            components: [],
          },
          { at: index }
        );
      }
    }
  });

  // Save custom fonts to project data before storing
  editor.on("storage:start:store", (data) => {
    const ds = editor.DataSources.get("custom-fonts");
    if (ds) {
      data.customFonts = ds.getRecords().map((record) => ({
        id: record.get("id"),
        family: record.get("family"),
        url: record.get("url"),
        subsets: record.get("subsets"),
        variants: record.get("variants"),
      }));
    }
  });

  // Load custom fonts from project data after canves is rendered
  editor.on("canvas:frame:load", (data) => {
    if (data.customFonts && data.customFonts.length > 0) {
      let ds = editor.DataSources.get("custom-fonts");
      if (!ds) {
        ds = editor.DataSources.add({ id: "custom-fonts", records: [] });
      }
      data.customFonts.forEach(
        (font: {
          id: string;
          family: string;
          url: string;
          subsets: string;
          variants: string;
        }) => {
          ds.addRecord(font);
        }
      );
    }

    const addCustomFontsOptions = () => {
      const fontProp = editor.StyleManager.getProperty(
        "typography",
        "font-family"
      );
      if (!fontProp) return;

      const options = (fontProp as unknown as FontProperty).getOptions();
      const ds = editor.DataSources.get("custom-fonts");

      if (ds) {
        const newOptions: FontOption[] = [];
        ds.getRecords().forEach((record) => {
          const family = record.get("family");
          const url = record.get("url");

          const insertLink = () => {
            const doc = editor.Canvas.getDocument();
            if (!doc || !doc.head) return;
            const head = doc.head;
            const existing = head.querySelector(`link[href="${url}"]`);
            if (!existing) {
              const linkEl = doc.createElement("link");
              linkEl.setAttribute("rel", "stylesheet");
              linkEl.setAttribute("href", url);
              linkEl.setAttribute("data-managed", "custom-font");
              head.appendChild(linkEl);
            }
          };

          insertLink();
          
          if (
            !options.some(
              (opt) => opt.id.toLowerCase() === family.toLowerCase()
            )
          ) {
            newOptions.push({ id: `${family}`, label: family });
          }
        });

        setTimeout(() => {
          (fontProp as unknown as FontProperty).setOptions(
            options.concat(newOptions)
          );
          editor.StyleManager.render();
        }, 1000);
        // 1s is a workaround to ensure the options are added to the property
        // otherwise the property will be empty and the options will not be added
      }
    };

    setTimeout(addCustomFontsOptions, 10);
  });
};
